import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import projectService from '../../services/projectService';
import actionService from '../../services/actionService';
import targetService from '../../services/targetService';
import blueTeamService from '../../services/blueTeamService';
import webTargetService from '../../services/webTargetService';
import webFindingService from '../../services/webFindingService';
import enhancedDebriefGeneratorService from '../../services/enhancedDebriefGeneratorService';
import debriefExportService from '../../services/debriefExportService';
import exportService from '../../services/exportService';
import reportTemplatesService from '../../services/reportTemplatesService';
import reportChartService from '../../services/reportChartService';
import ActionDetailModal from '../common/ActionDetailModal';
import { toast } from 'react-toastify';
import { useTheme } from '../../contexts/ThemeContext';
import { useLanguage } from '../../contexts/LanguageContext';
import PageHeader from '../common/PageHeader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartBar,
  faClock,
  faChartLine,
  faSearch,
  faBullseye,
  faMap,
  faRandom,
  faClipboardList,
  faWrench,
  faLightbulb,
  faBook,
  faGlobe,
  faFileExport,
  faFileAlt,
  faMapMarkedAlt,
  faArrowLeft
} from '@fortawesome/free-solid-svg-icons';

const DebriefViewer = () => {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const { darkMode: isDarkMode } = useTheme();
  const { t } = useLanguage();
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [exportFormat, setExportFormat] = useState('');
  const [showExportMenu, setShowExportMenu] = useState(false);
  const [showTemplateModal, setShowTemplateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [debrief, setDebrief] = useState(null);
  const [activeSection, setActiveSection] = useState('executive');
  const [project, setProject] = useState(null);
  const [expandedTargets, setExpandedTargets] = useState(new Set());
  const [expandedActions, setExpandedActions] = useState(new Set());
  const [selectedActionForModal, setSelectedActionForModal] = useState(null);
  const [reportTheme, setReportTheme] = useState('dark'); // 'light' or 'dark' for board presentation

  useEffect(() => {
    generateDebrief();
  }, [projectId]);

  const generateDebrief = async () => {
    try {
      setLoading(true);
      
      // Fetch all required data
      const [projectData, actions, targets, blueTeamStats] = await Promise.all([
        projectService.getProject(projectId),
        actionService.getActionsByProject(projectId),
        targetService.getTargetsByProject(projectId),
        blueTeamService.getProjectStats(projectId).catch(() => null)
      ]);

      setProject(projectData);

      // Fetch web testing data (optional, silent fail)
      let webData = null;
      try {
        const webTargets = await webTargetService.getAll(projectId);
        if (webTargets && webTargets.length > 0) {
          const webFindings = await webFindingService.getAll({ project: projectId });
          webData = { webTargets, webFindings: Array.isArray(webFindings) ? webFindings : [] };
        }
      } catch { /* web data not available */ }

      // Generate the enhanced debrief
      const generatedDebrief = enhancedDebriefGeneratorService.generateDebrief(
        projectData,
        actions,
        targets,
        [],
        blueTeamStats,
        webData
      );

      setDebrief(generatedDebrief);
      setLoading(false);
    } catch (error) {
      console.error('Error generating debrief:', error);
      toast.error('Failed to generate debrief');
      setLoading(false);
    }
  };

  const handleExport = async (format) => {
    if (!debrief) return;
    
    setExporting(true);
    setExportFormat(format);
    setShowExportMenu(false);
    
    try {
      await debriefExportService.exportDebrief(debrief, format, project.name);
      toast.success(`Debrief exported as ${format.toUpperCase()} successfully!`);
    } catch (error) {
      console.error('Export error:', error);
      toast.error(`Failed to export debrief as ${format.toUpperCase()}`);
    } finally {
      setExporting(false);
      setExportFormat('');
    }
  };

  const handlePreviewHTML = async () => {
    if (!debrief) return;
    setShowExportMenu(false);
    try {
      await debriefExportService.previewHTML(debrief);
    } catch (error) {
      console.error('Preview error:', error);
      toast.error('Failed to open preview');
    }
  };

  const handleExportMitreNavigator = async () => {
    if (!debrief || !project) return;

    setExporting(true);
    setShowExportMenu(false);

    try {
      // Extract actions from debrief
      const actions = [];

      // Get actions from timeline
      if (debrief.timeline) {
        debrief.timeline.forEach(event => {
          if (event.technique && event.technique !== 'N/A') {
            const [techniqueId, ...nameParts] = event.technique.split(' - ');
            actions.push({
              mitreAttackTechnique: {
                id: techniqueId,
                name: nameParts.join(' - '),
                tactic: event.tactic || 'Unknown'
              },
              result: event.result
            });
          }
        });
      }

      // Get actions from detailed actions if available
      if (debrief.detailedActions) {
        debrief.detailedActions.forEach(action => {
          if (action.techniqueDetails) {
            actions.push({
              mitreAttackTechnique: action.techniqueDetails,
              result: action.result
            });
          }
        });
      }

      await exportService.exportMitreNavigator(projectId, actions, project.name);
      toast.success('MITRE Navigator layer exported successfully!');
    } catch (error) {
      console.error('MITRE Navigator export error:', error);
      toast.error(`Failed to export MITRE Navigator layer: ${error.message || 'Unknown error'}`);
    } finally {
      setExporting(false);
    }
  };

  // Export using a specific template
  const handleTemplateExport = async (templateId) => {
    if (!debrief || !project) return;

    setExporting(true);
    setShowTemplateModal(false);

    try {
      let reportData;
      const timestamp = new Date().toISOString().split('T')[0];
      const baseFilename = `${templateId}-${project.name.replace(/\s+/g, '-')}-${timestamp}`;

      switch (templateId) {
        case 'executive-summary':
          reportData = reportTemplatesService.generateExecutiveSummary(debrief, project);
          break;
        case 'technical-detailed':
          reportData = reportTemplatesService.generateTechnicalReport(debrief, project);
          break;
        case 'compliance-audit':
          reportData = reportTemplatesService.generateComplianceReport(debrief, project, 'NIST');
          break;
        case 'incident-response':
          reportData = reportTemplatesService.generateIncidentResponseReport(debrief, project);
          break;
        case 'risk-assessment':
          reportData = reportTemplatesService.generateRiskAssessmentReport(debrief, project);
          break;
        case 'board-presentation':
          reportData = reportTemplatesService.generateBoardPresentation(debrief, project);
          break;
        default:
          throw new Error(`Unknown template: ${templateId}`);
      }

      // Convert report data to formatted content and download
      const theme = templateId === 'board-presentation' ? reportTheme : 'light';
      const content = generateReportContent(reportData, templateId, theme);
      const blob = new Blob([content], { type: 'text/html' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = `${baseFilename}.html`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      toast.success(`${reportData.title} exported successfully!`);
    } catch (error) {
      console.error('Template export error:', error);
      toast.error(`Failed to export report: ${error.message}`);
    } finally {
      setExporting(false);
    }
  };

  // Generate formatted HTML content from report data
  const generateReportContent = (reportData, templateId, theme = 'light') => {
    const isDark = theme === 'dark';

    const lightStyles = `
      body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 0; padding: 2rem; background: #f5f5f5; color: #333; line-height: 1.6; }
      .container { max-width: 1000px; margin: 0 auto; background: white; padding: 3rem; box-shadow: 0 0 20px rgba(0,0,0,0.1); }
      h1 { color: #1f2937; border-bottom: 3px solid #2563eb; padding-bottom: 1rem; }
      h2 { color: #374151; margin-top: 2rem; border-bottom: 1px solid #e5e7eb; padding-bottom: 0.5rem; }
      h3 { color: #4b5563; }
      .header { text-align: center; margin-bottom: 2rem; }
      .classification { background: #dc2626; color: white; padding: 0.5rem 1rem; display: inline-block; font-weight: bold; border-radius: 4px; }
      .subtitle { color: #6b7280; font-size: 1.25rem; }
      .section { margin: 2rem 0; padding: 1.5rem; background: #f9fafb; border-radius: 8px; }
      .metric-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; margin: 1rem 0; }
      .metric-card { background: white; padding: 1.5rem; border-radius: 8px; text-align: center; box-shadow: 0 1px 3px rgba(0,0,0,0.1); }
      .metric-value { font-size: 2rem; font-weight: bold; color: #2563eb; }
      .metric-label { color: #6b7280; font-size: 0.875rem; }
      table { width: 100%; border-collapse: collapse; margin: 1rem 0; }
      th, td { border: 1px solid #e5e7eb; padding: 0.75rem; text-align: left; }
      th { background: #f3f4f6; font-weight: 600; }
      .severity-critical { background: #fee2e2; color: #991b1b; }
      .severity-high { background: #fed7aa; color: #9a3412; }
      .severity-medium { background: #fef3c7; color: #92400e; }
      .severity-low { background: #d1fae5; color: #065f46; }
      .finding { margin: 1rem 0; padding: 1rem; border-radius: 8px; border-left: 4px solid; }
      .finding.critical { background: #fee2e2; border-color: #dc2626; }
      .finding.high { background: #fed7aa; border-color: #ea580c; }
      .slide { page-break-after: always; min-height: 600px; padding: 2rem; border: 1px solid #e5e7eb; margin: 2rem 0; background: white; }
      .slide-title { font-size: 2rem; color: #1f2937; text-align: center; margin-bottom: 2rem; }
      @media print { .container { box-shadow: none; } }
    `;

    const darkStyles = `
      body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif; margin: 0; padding: 2rem; background: #0f0f1a; color: #e2e8f0; line-height: 1.6; }
      .container { max-width: 1000px; margin: 0 auto; background: #1a1a2e; padding: 3rem; box-shadow: 0 0 40px rgba(0,0,0,0.5); border-radius: 12px; }
      h1 { color: #f1f5f9; border-bottom: 3px solid; border-image: linear-gradient(90deg, #6366f1, #8b5cf6, #a855f7) 1; padding-bottom: 1rem; }
      h2 { color: #e2e8f0; margin-top: 2rem; border-bottom: 1px solid #334155; padding-bottom: 0.5rem; }
      h3 { color: #cbd5e1; }
      .header { text-align: center; margin-bottom: 2rem; }
      .classification { background: linear-gradient(135deg, #dc2626, #b91c1c); color: white; padding: 0.5rem 1rem; display: inline-block; font-weight: bold; border-radius: 6px; letter-spacing: 0.05em; }
      .subtitle { color: #94a3b8; font-size: 1.25rem; }
      .section { margin: 2rem 0; padding: 1.5rem; background: #16213e; border-radius: 12px; border: 1px solid #1e3a5f; }
      .metric-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; margin: 1rem 0; }
      .metric-card { background: linear-gradient(135deg, #1e293b, #0f172a); padding: 1.5rem; border-radius: 12px; text-align: center; border: 1px solid #334155; box-shadow: 0 4px 12px rgba(0,0,0,0.3); }
      .metric-value { font-size: 2.5rem; font-weight: bold; background: linear-gradient(135deg, #60a5fa, #a78bfa); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
      .metric-label { color: #94a3b8; font-size: 0.875rem; margin-top: 0.25rem; }
      table { width: 100%; border-collapse: collapse; margin: 1rem 0; }
      th, td { border: 1px solid #334155; padding: 0.75rem; text-align: left; }
      th { background: #0f172a; font-weight: 600; color: #94a3b8; text-transform: uppercase; font-size: 0.75rem; letter-spacing: 0.05em; }
      td { color: #cbd5e1; }
      .severity-critical { background: rgba(220,38,38,0.15); color: #fca5a5; }
      .severity-high { background: rgba(234,88,12,0.15); color: #fdba74; }
      .severity-medium { background: rgba(245,158,11,0.15); color: #fde68a; }
      .severity-low { background: rgba(34,197,94,0.15); color: #86efac; }
      .finding { margin: 1rem 0; padding: 1rem; border-radius: 12px; border-left: 4px solid; }
      .finding.critical { background: rgba(220,38,38,0.1); border-color: #dc2626; }
      .finding.high { background: rgba(234,88,12,0.1); border-color: #ea580c; }
      .slide { page-break-after: always; min-height: 600px; padding: 3rem; border: 1px solid #1e3a5f; margin: 2rem 0; background: linear-gradient(180deg, #1a1a2e, #16213e); border-radius: 12px; }
      .slide-title { font-size: 2rem; text-align: center; margin-bottom: 2rem; background: linear-gradient(135deg, #60a5fa, #c084fc); -webkit-background-clip: text; -webkit-text-fill-color: transparent; font-weight: 700; }
      p { color: #cbd5e1; }
      ul, ol { color: #cbd5e1; }
      li { margin: 0.5rem 0; }
      dt { color: #94a3b8; }
      dd { color: #e2e8f0; margin-bottom: 0.75rem; }
      @media print { .container { box-shadow: none; } body { background: #1a1a2e; } }
    `;

    const styles = isDark ? darkStyles : lightStyles;

    let content = `<!DOCTYPE html><html><head><meta charset="UTF-8"><title>${reportData.title}</title><style>${styles}</style></head><body><div class="container">`;

    // Header
    content += `<div class="header">`;
    content += `<h1>${reportData.title}</h1>`;
    if (templateId !== 'board-presentation') {
      if (reportData.subtitle) content += `<div class="subtitle">${reportData.subtitle}</div>`;
      if (reportData.classification) content += `<div class="classification">${reportData.classification}</div>`;
      content += `<p>Generated: ${new Date(reportData.date).toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: 'numeric' })}</p>`;
    }
    content += `</div>`;

    // Handle different template formats
    if (templateId === 'board-presentation' && reportData.slides) {
      // Board presentation format
      reportData.slides.forEach(slide => {
        content += `<div class="slide">`;
        content += `<div class="slide-title">${slide.title}</div>`;
        content += formatSlideContent(slide);
        content += `</div>`;
      });
    } else if (reportData.sections) {
      // Standard section format
      reportData.sections.forEach(section => {
        content += `<div class="section">`;
        content += `<h2>${section.title}</h2>`;
        if (section.subsections) {
          section.subsections.forEach(sub => {
            content += `<h3>${sub.title}</h3>`;
            content += formatSectionContent(sub.content);
          });
        } else {
          content += formatSectionContent(section.content);
        }
        content += `</div>`;
      });
    }

    content += `</div></body></html>`;
    return content;
  };

  // Format a badge for severity/priority/risk values
  const formatBadge = (value) => {
    if (typeof value !== 'string') return value;
    const lower = value.toLowerCase();
    const badgeStyles = {
      critical: 'background:#fee2e2;color:#991b1b;border:1px solid #fca5a5;',
      immediate: 'background:#fee2e2;color:#991b1b;border:1px solid #fca5a5;',
      high: 'background:#fed7aa;color:#9a3412;border:1px solid #fdba74;',
      medium: 'background:#fef3c7;color:#92400e;border:1px solid #fde68a;',
      low: 'background:#d1fae5;color:#065f46;border:1px solid #6ee7b7;',
      'non-compliant': 'background:#fee2e2;color:#991b1b;border:1px solid #fca5a5;',
      'non-conformity': 'background:#fee2e2;color:#991b1b;border:1px solid #fca5a5;',
      partial: 'background:#fef3c7;color:#92400e;border:1px solid #fde68a;',
      compliant: 'background:#d1fae5;color:#065f46;border:1px solid #6ee7b7;',
      conforming: 'background:#d1fae5;color:#065f46;border:1px solid #6ee7b7;',
      pending: 'background:#fef3c7;color:#92400e;border:1px solid #fde68a;'
    };
    const style = badgeStyles[lower];
    if (style) return `<span style="${style}padding:2px 8px;border-radius:9999px;font-size:0.75rem;font-weight:600;white-space:nowrap;">${value}</span>`;
    return value;
  };

  // Columns that should be rendered as badges
  const isBadgeColumn = (key) => {
    const badgeCols = ['severity', 'priority', 'status', 'risk', 'currentState', 'currentstate', 'treatment'];
    return badgeCols.includes(key.toLowerCase());
  };

  // Format section content based on type
  const formatSectionContent = (contentData) => {
    if (!contentData) return '';

    if (typeof contentData === 'string') {
      return `<p>${contentData.replace(/\n/g, '</p><p>')}</p>`;
    }

    if (Array.isArray(contentData)) {
      if (contentData.length === 0) return '<p>No data available</p>';

      // Check if it's an array of objects with common structure
      if (typeof contentData[0] === 'object') {
        const keys = Object.keys(contentData[0]);
        let html = '<table><thead><tr>';
        keys.forEach(key => {
          html += `<th>${key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1')}</th>`;
        });
        html += '</tr></thead><tbody>';
        contentData.forEach(item => {
          html += '<tr>';
          keys.forEach(key => {
            let value = item[key];
            if (typeof value === 'object' && value !== null) value = JSON.stringify(value);
            // Apply badge formatting for severity/priority/status columns
            if (isBadgeColumn(key) && typeof value === 'string') {
              html += `<td>${formatBadge(value)}</td>`;
            } else {
              html += `<td>${value || '-'}</td>`;
            }
          });
          html += '</tr>';
        });
        html += '</tbody></table>';
        return html;
      }

      return `<ul>${contentData.map(item => `<li>${item}</li>`).join('')}</ul>`;
    }

    if (typeof contentData === 'object') {
      // Handle pre-rendered SVG content (e.g. MITRE heatmap)
      if (contentData._svgContent) {
        const summaryHtml = contentData.summary ? `<p style="margin-bottom:1rem;color:#6b7280;">${contentData.summary}</p>` : '';
        return `${summaryHtml}<div style="overflow-x:auto;">${contentData._svgContent}</div>`;
      }

      // Handle Chart.js format data (trend charts)
      if (contentData._chartType === 'bar-chart-js' || (contentData.labels && contentData.datasets)) {
        return reportChartService.chartJsToSvg(contentData, { width: 500, height: 300 });
      }

      // Handle special object types
      if (contentData.score !== undefined && contentData.rating !== undefined) {
        // Security score format - render gauge + metrics
        const gaugeScore = contentData.detectionRate || 0;
        const grade = contentData.score;
        const gaugeSvg = reportChartService.generateSecurityPostureGauge(grade, gaugeScore);
        return `
          <div style="text-align:center;margin-bottom:1.5rem;">
            ${gaugeSvg}
          </div>
          <div class="metric-grid">
            <div class="metric-card">
              <div class="metric-value">${contentData.score}</div>
              <div class="metric-label">Security Grade</div>
            </div>
            <div class="metric-card">
              <div class="metric-value">${contentData.rating}</div>
              <div class="metric-label">Rating</div>
            </div>
            <div class="metric-card">
              <div class="metric-value">${contentData.detectionRate}%</div>
              <div class="metric-label">Detection Rate</div>
            </div>
          </div>
          ${contentData.factors ? `
            <h4>${t('debrief.contributingFactors') || 'Contributing Factors'}</h4>
            <table>
              <thead><tr><th>${t('debrief.factor') || 'Factor'}</th><th>${t('common.value') || 'Value'}</th><th>${t('debrief.weight') || 'Weight'}</th></tr></thead>
              <tbody>
                ${contentData.factors.map(f => `<tr><td>${f.name}</td><td>${f.value}</td><td>${f.weight}%</td></tr>`).join('')}
              </tbody>
            </table>
          ` : ''}
        `;
      }

      if (contentData.critical !== undefined || contentData.high !== undefined) {
        // Findings format - add donut chart
        const criticalLabel = t('findings.critical') || 'Critical';
        const highLabel = t('findings.high') || 'High';
        const mediumLabel = t('findings.medium') || 'Medium';
        const lowLabel = t('findings.low') || 'Low';
        const segments = [
          { label: criticalLabel, value: contentData.critical?.length || contentData.critical || 0, color: '#dc2626' },
          { label: highLabel, value: contentData.high?.length || contentData.high || 0, color: '#ea580c' },
          { label: mediumLabel, value: contentData.medium?.length || contentData.medium || 0, color: '#f59e0b' },
          { label: lowLabel, value: contentData.low?.length || contentData.low || 0, color: '#22c55e' }
        ].filter(s => s.value > 0);

        const donutSvg = segments.length > 0
          ? reportChartService.generateDonutChart(segments, { width: 280, height: 280, title: t('debrief.sections.findings') || 'Findings Distribution' })
          : '';

        return `
          ${donutSvg ? `<div style="text-align:center;margin-bottom:1.5rem;">${donutSvg}</div>` : ''}
          <div class="metric-grid">
            <div class="metric-card severity-critical"><div class="metric-value">${contentData.critical?.length || contentData.critical || 0}</div><div class="metric-label">${criticalLabel}</div></div>
            <div class="metric-card severity-high"><div class="metric-value">${contentData.high?.length || contentData.high || 0}</div><div class="metric-label">${highLabel}</div></div>
            <div class="metric-card severity-medium"><div class="metric-value">${contentData.medium?.length || contentData.medium || 0}</div><div class="metric-label">${mediumLabel}</div></div>
            <div class="metric-card severity-low"><div class="metric-value">${contentData.low?.length || contentData.low || 0}</div><div class="metric-label">${lowLabel}</div></div>
          </div>
          ${contentData.critical && Array.isArray(contentData.critical) ? contentData.critical.map(f => `
            <div class="finding critical">
              <h4>${f.title}</h4>
              <p>${f.description || f.impact || ''}</p>
            </div>
          `).join('') : ''}
        `;
      }

      // Risk matrix format
      if (contentData.axes && contentData.risks) {
        const risks = contentData.risks || [];
        let html = `<table><thead><tr><th>${t('debrief.risk') || 'Risk'}</th><th>${t('debrief.likelihood') || 'Likelihood'}</th><th>${t('debrief.impact') || 'Impact'}</th><th>${t('common.severity') || 'Severity'}</th></tr></thead><tbody>`;
        risks.forEach(r => {
          html += `<tr><td>${r.name}</td><td>${r.likelihood}/5</td><td>${r.impact}/5</td><td>${formatBadge(r.severity)}</td></tr>`;
        });
        html += '</tbody></table>';
        return html;
      }

      // Generic object format
      let html = '<dl>';
      Object.entries(contentData).forEach(([key, value]) => {
        if (key.startsWith('_')) return; // Skip internal metadata keys
        const label = key.charAt(0).toUpperCase() + key.slice(1).replace(/([A-Z])/g, ' $1');
        let displayValue = value;
        if (Array.isArray(value)) {
          if (value.length > 0 && typeof value[0] === 'object') {
            // Render nested array of objects as a sub-table
            displayValue = formatSectionContent(value);
          } else {
            displayValue = value.join(', ');
          }
        } else if (typeof value === 'object' && value !== null) {
          displayValue = formatSectionContent(value);
        }
        if (isBadgeColumn(key) && typeof displayValue === 'string') {
          displayValue = formatBadge(displayValue);
        }
        html += `<dt><strong>${label}:</strong></dt><dd>${displayValue}</dd>`;
      });
      html += '</dl>';
      return html;
    }

    return String(contentData);
  };

  // Format slide content for board presentation
  const formatSlideContent = (slide) => {
    if (!slide.content) return '';

    switch (slide.type) {
      case 'title':
        return `
          <div style="text-align: center; padding: 4rem;">
            <h2>${slide.content.headline || ''}</h2>
            <p style="font-size: 1.25rem; color: #6b7280;">${slide.content.period || ''}</p>
            ${slide.content.classification ? `<div class="classification">${slide.content.classification}</div>` : ''}
          </div>
        `;

      case 'metrics':
        return `
          <div class="metric-grid">
            ${slide.content.map(m => `
              <div class="metric-card">
                <div class="metric-value" style="color: ${m.trend === 'positive' ? '#10b981' : m.trend === 'negative' ? '#ef4444' : '#2563eb'}">
                  ${m.value}
                </div>
                <div class="metric-label">${m.label}</div>
              </div>
            `).join('')}
          </div>
        `;

      case 'scorecard':
        const gradeColor = slide.content.overallGrade === 'A' ? '#10b981' :
              slide.content.overallGrade === 'B' ? '#3b82f6' :
              slide.content.overallGrade === 'C' ? '#f59e0b' : '#ef4444';
        const impactBullets = (slide.content.businessImpact || []).map(b =>
          `<li style="margin:0.75rem 0;"><strong>${b.type}:</strong> ${b.text}</li>`
        ).join('');
        return `
          <div style="text-align: center;">
            <div style="font-size: 6rem; font-weight: bold; color: ${gradeColor};">${slide.content.overallGrade}</div>
            <div style="font-size: 1.5rem; color: #6b7280;">${slide.content.rating}</div>
          </div>
          ${impactBullets ? `<div style="margin-top:2rem;"><h3 style="font-size:1.25rem;margin-bottom:0.75rem;">Business Impact</h3><ul style="font-size:1.1rem;text-align:left;">${impactBullets}</ul></div>` : ''}
        `;

      case 'list':
        return `
          <ul style="font-size: 1.25rem;">
            ${slide.content.map(item => `
              <li style="margin: 1rem 0; ${item.severity === 'Critical' ? 'color: #dc2626;' : ''}">
                <strong>${item.severity || ''}:</strong> ${item.description}
              </li>
            `).join('')}
          </ul>
        `;

      case 'action-items':
        return `
          <table>
            <thead><tr><th>Action</th><th>Owner</th><th>Deadline</th><th>Status</th></tr></thead>
            <tbody>
              ${slide.content.map(item => `
                <tr>
                  <td>${item.action}</td>
                  <td>${item.owner}</td>
                  <td>${item.deadline}</td>
                  <td>${item.status}</td>
                </tr>
              `).join('')}
            </tbody>
          </table>
        `;

      case 'table':
        if (!slide.content || slide.content.length === 0) return '<p>No data</p>';
        const keys = Object.keys(slide.content[0] || {});
        return `
          <table>
            <thead><tr>${keys.map(k => `<th>${k.charAt(0).toUpperCase() + k.slice(1)}</th>`).join('')}</tr></thead>
            <tbody>
              ${slide.content.map(row => `<tr>${keys.map(k => {
                const val = row[k] || '';
                return `<td>${isBadgeColumn(k) ? formatBadge(val) : val}</td>`;
              }).join('')}</tr>`).join('')}
            </tbody>
          </table>
        `;

      case 'chart':
        // Render Chart.js format data as SVG
        if (slide.content && (slide.content._chartType === 'bar-chart-js' || (slide.content.labels && slide.content.datasets))) {
          return `<div style="text-align:center;">${reportChartService.chartJsToSvg(slide.content, { width: 600, height: 350 })}</div>`;
        }
        return formatSectionContent(slide.content);

      case 'heatmap':
        // Render MITRE heatmap SVG with fullscreen button
        if (slide.content?._svgContent) {
          const summaryHtml = slide.content.summary ? `<p style="margin-bottom:1rem;color:#6b7280;text-align:center;">${slide.content.summary}</p>` : '';
          const fullscreenBtn = `<div style="text-align:right;margin-bottom:0.5rem;">
            <button onclick="(function(el){el=el.closest('.heatmap-wrap');if(el.requestFullscreen)el.requestFullscreen();else if(el.webkitRequestFullscreen)el.webkitRequestFullscreen();})(this)" style="padding:6px 16px;background:#2563eb;color:#fff;border:none;border-radius:6px;cursor:pointer;font-size:13px;">Fullscreen</button>
          </div>`;
          return `${summaryHtml}<div class="heatmap-wrap" style="background:#fafafa;border-radius:8px;">${fullscreenBtn}<div style="overflow-x:auto;">${slide.content._svgContent}</div></div>`;
        }
        return formatSectionContent(slide.content);

      default:
        return formatSectionContent(slide.content);
    }
  };

  const toggleTargetExpansion = (targetKey) => {
    const newExpanded = new Set(expandedTargets);
    if (newExpanded.has(targetKey)) {
      newExpanded.delete(targetKey);
    } else {
      newExpanded.add(targetKey);
    }
    setExpandedTargets(newExpanded);
  };

  const toggleActionExpansion = (actionId) => {
    const newExpanded = new Set(expandedActions);
    if (newExpanded.has(actionId)) {
      newExpanded.delete(actionId);
    } else {
      newExpanded.add(actionId);
    }
    setExpandedActions(newExpanded);
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (!debrief) {
    return (
      <div className={`border px-4 py-3 rounded ${isDarkMode ? 'bg-red-900/30 border-red-500 text-red-300' : 'bg-red-50 border-red-200 text-red-700'}`}>
        {t('debrief.failedToGenerate') !== 'debrief.failedToGenerate' ? t('debrief.failedToGenerate') : 'Failed to generate debrief. Please try again.'}
      </div>
    );
  }

  const sections = [
    { id: 'executive', label: t('debrief.sections.executive'), icon: faChartBar },
    { id: 'timeline', label: t('debrief.sections.timeline'), icon: faClock },
    { id: 'statistics', label: t('debrief.sections.statistics'), icon: faChartLine },
    { id: 'findings', label: t('debrief.sections.findings'), icon: faSearch },
    { id: 'targets', label: t('debrief.sections.targets'), icon: faBullseye },
    { id: 'mitre', label: t('debrief.sections.mitre'), icon: faMap },
    { id: 'attackpath', label: t('debrief.sections.attackPath'), icon: faRandom },
    { id: 'actions', label: t('debrief.sections.actions'), icon: faClipboardList },
    { id: 'technical', label: t('debrief.sections.technical'), icon: faWrench },
    { id: 'recommendations', label: t('debrief.sections.recommendations'), icon: faLightbulb },
    { id: 'lessons', label: t('debrief.sections.lessons'), icon: faBook },
    { id: 'future', label: t('debrief.sections.future'), icon: faGlobe }
  ];

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <Link to={`/projects/${projectId}`} className="text-blue-600 hover:text-blue-800 flex items-center text-sm mb-2">
        <FontAwesomeIcon icon={faArrowLeft} className="mr-1" />
        {t('debrief.backToProject')}
      </Link>
      <PageHeader
        title={t('debrief.title')}
        subtitle={<>{project?.name} - {project?.client}<p className="text-sm text-red-600 font-medium mt-1">{debrief.metadata.classification}</p></>}
      >
        {/* Report Templates Button */}
        <button
          onClick={() => setShowTemplateModal(true)}
          disabled={exporting}
          className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:bg-indigo-400 flex items-center"
        >
          <FontAwesomeIcon icon={faFileAlt} className="mr-2" />{t('debrief.reportTemplates')}
        </button>

        {/* Export Options */}
        <div className="relative inline-block">
          <button
            onClick={() => setShowExportMenu(!showExportMenu)}
            disabled={exporting}
            className="bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700 disabled:bg-blue-400 flex items-center"
          >
            <FontAwesomeIcon icon={faFileExport} className="mr-2" />{t('debrief.exportDebrief')}
            <svg className="ml-2 h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          {showExportMenu && !exporting && (
            <div className={`absolute right-0 mt-2 w-48 rounded-md shadow-lg z-10 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
              <button
                onClick={() => handleExport('markdown')}
                className={`block w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                Markdown (.md)
              </button>
              <button
                onClick={() => handleExport('html')}
                className={`block w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                HTML (.html)
              </button>
              <button
                onClick={() => handleExport('docx')}
                className={`block w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                Word Document (.docx)
              </button>
              <button
                onClick={handleExportMitreNavigator}
                className={`block w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                MITRE Navigator Layer (.json)
              </button>
              <div className={`border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}></div>
              <button
                onClick={handlePreviewHTML}
                className={`block w-full text-left px-4 py-2 text-sm ${isDarkMode ? 'text-gray-200 hover:bg-gray-700' : 'text-gray-700 hover:bg-gray-100'}`}
              >
                {t('debrief.previewHTML') !== 'debrief.previewHTML' ? t('debrief.previewHTML') : 'Preview as HTML'}
              </button>
            </div>
          )}
        </div>

        <button
          onClick={handleExportMitreNavigator}
          disabled={exporting}
          className="bg-purple-600 text-white px-4 py-2 rounded-md hover:bg-purple-700 disabled:bg-purple-400"
        >
          <FontAwesomeIcon icon={faMapMarkedAlt} className="mr-2" />{t('debrief.exportMitreLayer')}
        </button>
      </PageHeader>

      {/* Report Template Selection Modal */}
      {showTemplateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className={`rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <div className={`p-6 border-b ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <div className="flex justify-between items-center">
                <h2 className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('debrief.selectTemplate')}</h2>
                <button
                  onClick={() => setShowTemplateModal(false)}
                  className={`p-2 rounded-full ${isDarkMode ? 'hover:bg-gray-700 text-gray-400' : 'hover:bg-gray-100 text-gray-500'}`}
                >
                  ✕
                </button>
              </div>
              <p className={`mt-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Choose a report template tailored for your audience
              </p>
            </div>

            <div className="p-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {reportTemplatesService.templates.map(template => (
                <div
                  key={template.id}
                  onClick={() => setSelectedTemplate(template.id)}
                  className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    selectedTemplate === template.id
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/30'
                      : isDarkMode
                        ? 'border-gray-700 hover:border-gray-600 bg-gray-700/50'
                        : 'border-gray-200 hover:border-gray-300 bg-gray-50'
                  }`}
                >
                  <div className="flex items-start justify-between">
                    <div>
                      <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                        {template.name}
                      </h3>
                      <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {template.description}
                      </p>
                      <div className="mt-2">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-700'
                        }`}>
                          👥 {template.audience}
                        </span>
                      </div>
                    </div>
                    {selectedTemplate === template.id && (
                      <div className="text-indigo-600 text-xl">✓</div>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Theme toggle for board presentation */}
            {selectedTemplate === 'board-presentation' && (
              <div className={`px-6 py-3 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                <div className="flex items-center justify-between">
                  <span className={`text-sm font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Presentation Theme</span>
                  <div className="flex rounded-lg overflow-hidden border border-gray-300 dark:border-gray-600">
                    <button
                      onClick={() => setReportTheme('light')}
                      className={`px-3 py-1.5 text-sm ${reportTheme === 'light' ? 'bg-indigo-600 text-white' : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-white text-gray-700'}`}
                    >
                      Light
                    </button>
                    <button
                      onClick={() => setReportTheme('dark')}
                      className={`px-3 py-1.5 text-sm ${reportTheme === 'dark' ? 'bg-indigo-600 text-white' : isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-white text-gray-700'}`}
                    >
                      Dark
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className={`p-6 border-t flex justify-end space-x-3 ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
              <button
                onClick={() => setShowTemplateModal(false)}
                className={`px-4 py-2 rounded-md ${isDarkMode ? 'bg-gray-700 text-gray-200 hover:bg-gray-600' : 'bg-gray-200 text-gray-700 hover:bg-gray-300'}`}
              >
                Cancel
              </button>
              <button
                onClick={() => selectedTemplate && handleTemplateExport(selectedTemplate)}
                disabled={!selectedTemplate || exporting}
                className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed"
              >
                {exporting ? 'Generating...' : 'Generate Report'}
              </button>
            </div>
          </div>
        </div>
      )}

      {exporting && (
        <div className="mb-4 bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-700 mr-3"></div>
            Exporting debrief as {exportFormat || 'MITRE Navigator layer'}...
          </div>
        </div>
      )}

      <div className="flex gap-6">
        {/* Sidebar Navigation */}
        <div className="w-64 flex-shrink-0">
          <div className={`rounded-lg shadow-md p-4 sticky top-4 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
            <h3 className={`font-semibold mb-4 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{t('debrief.sections.title')}</h3>
            <nav className="space-y-2">
              {sections.map(section => (
                <button
                  key={section.id}
                  onClick={() => setActiveSection(section.id)}
                  className={`w-full text-left px-3 py-2 rounded-md transition-colors flex items-center gap-2 ${
                    activeSection === section.id
                      ? isDarkMode
                        ? 'bg-blue-900 text-blue-300 font-medium'
                        : 'bg-blue-100 text-blue-700 font-medium'
                      : isDarkMode
                        ? 'text-gray-300 hover:bg-gray-700'
                        : 'hover:bg-gray-100'
                  }`}
                >
                  <FontAwesomeIcon icon={section.icon} className="text-blue-800 dark:text-blue-400 w-4" />
                  {section.label}
                </button>
              ))}
            </nav>
          </div>
        </div>

        {/* Main Content */}
        <div className={`flex-1 rounded-lg shadow-md p-6 ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
          {/* Executive Summary */}
          {activeSection === 'executive' && (
            <div>
              <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faChartBar} className="text-blue-800" />
                {t('debrief.sections.executive')}
              </h2>

              <div className="mb-6">
                <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{t('debrief.overview')}</h3>
                <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{debrief.executiveSummary.overview}</p>
              </div>

              <div className="mb-6">
                <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{t('debrief.engagementScope')}</h3>
                <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{debrief.executiveSummary.scope}</p>
              </div>

              <div className="mb-6">
                <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{t('debrief.keyStatistics')}</h3>
                <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                  {Object.entries(debrief.executiveSummary.keyStatistics).map(([key, value]) => {
                    const statLabels = {
                      totalActions: t('debrief.totalActions') || 'Total Actions',
                      successRate: t('debrief.successRate') || 'Success Rate',
                      detectionRate: t('debrief.detectionRate') || 'Detection Rate',
                      criticalFindings: t('debrief.criticalFindings') || 'Critical Findings',
                      highFindings: t('debrief.highFindings') || 'High Findings',
                      mediumFindings: t('debrief.mediumFindings') || 'Medium Findings',
                      lowFindings: t('debrief.lowFindings') || 'Low Findings',
                      uniqueTechniques: t('executive.mitreTechniques') || 'Unique Techniques',
                      targetsCompromised: t('reports.targetsCompromised') || 'Targets Compromised'
                    };
                    return (
                    <div key={key} className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <div className="text-2xl font-bold text-blue-600">{value}</div>
                      <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {statLabels[key] || key.replace(/([A-Z])/g, ' $1').trim()}
                      </div>
                    </div>
                    );
                  })}
                </div>
              </div>

              <div className="mb-6">
                <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{t('debrief.mainFindings')}</h3>
                <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{debrief.executiveSummary.mainFindings}</p>
              </div>

              <div className="mb-6">
                <h3 className={`text-lg font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{t('debrief.overallRisk')}</h3>
                <div className={`p-4 rounded-lg ${
                  debrief.executiveSummary.overallRisk.level === 'CRITICAL' ? (isDarkMode ? 'bg-red-900/30 border-red-500' : 'bg-red-50 border-red-200') :
                  debrief.executiveSummary.overallRisk.level === 'HIGH' ? (isDarkMode ? 'bg-orange-900/30 border-orange-500' : 'bg-orange-50 border-orange-200') :
                  debrief.executiveSummary.overallRisk.level === 'MEDIUM' ? (isDarkMode ? 'bg-yellow-900/30 border-yellow-500' : 'bg-yellow-50 border-yellow-200') :
                  (isDarkMode ? 'bg-green-900/30 border-green-500' : 'bg-green-50 border-green-200')
                } border`}>
                  <div className={`text-lg font-bold ${
                    debrief.executiveSummary.overallRisk.level === 'CRITICAL' ? 'text-red-500' :
                    debrief.executiveSummary.overallRisk.level === 'HIGH' ? 'text-orange-500' :
                    debrief.executiveSummary.overallRisk.level === 'MEDIUM' ? 'text-yellow-500' :
                    'text-green-500'
                  }`}>
                    {debrief.executiveSummary.overallRisk.level}
                  </div>
                  <p className={`mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {debrief.executiveSummary.overallRisk.description}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Actions by Target */}
          {activeSection === 'targets' && debrief.actionsByTarget && (
            <div>
              <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faBullseye} className="text-blue-800" />
                {t('debrief.actionsByTarget')}
              </h2>

              <div className="space-y-4">
                {Object.entries(debrief.actionsByTarget).map(([targetKey, targetData]) => (
                  <div key={targetKey} className={`border rounded-lg overflow-hidden ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                    <div
                      className={`p-4 cursor-pointer ${isDarkMode ? 'bg-gray-700 hover:bg-gray-650' : 'bg-gray-50 hover:bg-gray-100'}`}
                      onClick={() => toggleTargetExpansion(targetKey)}
                    >
                      <div className="flex justify-between items-center">
                        <div>
                          <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                            {targetData.value}
                          </h3>
                          {targetData.description && (
                            <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{targetData.description}</p>
                          )}
                        </div>
                        <div className="flex items-center space-x-4">
                          <div className="text-right">
                            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('actions.totalActions')}</div>
                            <div className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{targetData.actions.length}</div>
                          </div>
                          <div className="text-right">
                            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('reports.successRate')}</div>
                            <div className="text-xl font-bold text-green-500">
                              {targetData.actions.length > 0
                                ? Math.round((targetData.successful / targetData.actions.length) * 100)
                                : 0}%
                            </div>
                          </div>
                          <svg
                            className={`w-5 h-5 transform transition-transform ${isDarkMode ? 'text-gray-400' : 'text-gray-600'} ${
                              expandedTargets.has(targetKey) ? 'rotate-180' : ''
                            }`}
                            fill="none" stroke="currentColor" viewBox="0 0 24 24"
                          >
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                          </svg>
                        </div>
                      </div>
                    </div>

                    {expandedTargets.has(targetKey) && (
                      <div className={`p-4 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                        <div className="grid grid-cols-4 gap-4 mb-4">
                          <div className={`p-3 rounded ${isDarkMode ? 'bg-green-900/30' : 'bg-green-50'}`}>
                            <div className="text-2xl font-bold text-green-500">{targetData.successful}</div>
                            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('actions.successful')}</div>
                          </div>
                          <div className={`p-3 rounded ${isDarkMode ? 'bg-yellow-900/30' : 'bg-yellow-50'}`}>
                            <div className="text-2xl font-bold text-yellow-500">{targetData.detected}</div>
                            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('actions.detected')}</div>
                          </div>
                          <div className={`p-3 rounded ${isDarkMode ? 'bg-red-900/30' : 'bg-red-50'}`}>
                            <div className="text-2xl font-bold text-red-500">{targetData.blocked}</div>
                            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('actions.blocked')}</div>
                          </div>
                          <div className={`p-3 rounded ${isDarkMode ? 'bg-purple-900/30' : 'bg-purple-50'}`}>
                            <div className="text-2xl font-bold text-purple-500">{targetData.techniques.size}</div>
                            <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('attackChain.techniquesUsed')}</div>
                          </div>
                        </div>

                        <h4 className={`font-semibold mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{t('debrief.actionTimeline')}</h4>
                        <div className="space-y-2">
                          {targetData.actions.map((action, index) => (
                            <div
                              key={action.id || index}
                              className={`p-3 rounded cursor-pointer ${isDarkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-gray-50 hover:bg-gray-100'}`}
                              onClick={() => setSelectedActionForModal(action.originalAction || action)}
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <h5 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{action.title}</h5>
                                  <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                    {new Date(action.timestamp).toLocaleString()} {action.operator && `\u2022 ${action.operator}`}
                                  </p>
                                  {action.technique && (
                                    <p className="text-sm text-purple-500 mt-1">{action.technique}</p>
                                  )}
                                </div>
                                <span className={`px-2 py-1 rounded text-xs font-medium ${
                                  action.result === 'successful' ? (isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800') :
                                  action.result === 'detected' ? (isDarkMode ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-100 text-yellow-800') :
                                  action.result === 'blocked' ? (isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-800') :
                                  (isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-800')
                                }`}>
                                  {action.result}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* MITRE Analysis */}
          {activeSection === 'mitre' && debrief.mitreAnalysis && (
            <div>
              <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faMap} className="text-blue-800" />
                {t('debrief.mitreAnalysis')}
              </h2>

              <div className="space-y-4">
                {debrief.mitreAnalysis.map((technique) => (
                  <div
                    key={technique.technique}
                    className={`border rounded-lg p-4 ${
                      technique.severity === 'critical' ? (isDarkMode ? 'bg-red-900/20 border-red-500' : 'bg-red-50 border-red-200') :
                      technique.severity === 'high' ? (isDarkMode ? 'bg-orange-900/20 border-orange-500' : 'bg-orange-50 border-orange-200') :
                      (isDarkMode ? 'bg-purple-900/20 border-purple-500' : 'bg-purple-50 border-purple-200')
                    }`}
                  >
                    <div className="flex justify-between items-start mb-3">
                      <div>
                        <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                          {technique.technique} - {technique.name}
                        </h3>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {t('actions.tactic')}: {technique.tactic} &bull; {technique.severity.toUpperCase()}
                        </p>
                      </div>
                      <div className="flex space-x-3">
                        <div className="text-center">
                          <div className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{technique.attempts}</div>
                          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('debrief.attempts')}</div>
                        </div>
                        <div className="text-center">
                          <div className="text-2xl font-bold text-green-500">
                            {Math.round(technique.successRate * 100)}%
                          </div>
                          <div className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{t('reports.successRate')}</div>
                        </div>
                      </div>
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{t('debrief.relatedActions')}</h4>
                        <div className="space-y-1">
                          {technique.actions.slice(0, 3).map((action, i) => (
                            <div
                              key={i}
                              className={`text-sm cursor-pointer hover:underline ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}
                              onClick={() => setSelectedActionForModal(action.originalAction || action)}
                            >
                              &bull; {action.title} ({action.result})
                            </div>
                          ))}
                          {technique.actions.length > 3 && (
                            <div className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                              ...{t('common.andMore', { count: technique.actions.length - 3 }) !== 'common.andMore' ? t('common.andMore', { count: technique.actions.length - 3 }) : `and ${technique.actions.length - 3} more`}
                            </div>
                          )}
                        </div>
                      </div>
                      <div>
                        <h4 className={`font-medium mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{t('debrief.engagementStatistics')}</h4>
                        <div className={`text-sm space-y-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          <div>{t('actions.successful')}: {technique.successful}</div>
                          <div>{t('actions.detected')}: {technique.detected}</div>
                          <div>{t('actions.blocked')}: {technique.blocked}</div>
                          <div>{t('actions.operator')}: {technique.operators.length}</div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Attack Path Visualization */}
          {activeSection === 'attackpath' && debrief.attackPath && (
            <div>
              <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faRandom} className="text-blue-800" />
                {t('debrief.attackPathAnalysis')}
              </h2>

              <div className="mb-6">
                <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
                  <h3 className={`font-semibold mb-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{t('debrief.attackChainCompleteness')}</h3>
                  <div className="flex items-center mb-3">
                    <div className={`flex-1 rounded-full h-4 mr-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'}`}>
                      <div
                        className="bg-blue-600 h-4 rounded-full"
                        style={{ width: `${debrief.attackPath.completeness}%` }}
                      ></div>
                    </div>
                    <span className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{debrief.attackPath.completeness}%</span>
                  </div>
                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                    {t('debrief.executedPhases')}: {debrief.attackPath.executedPhases} / {debrief.attackPath.totalPhases}
                  </p>
                </div>
              </div>

              <div className="mb-6">
                <h3 className={`font-semibold mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{t('debrief.executedPhases')}</h3>
                <div className="space-y-3">
                  {debrief.attackPath.phases.map((phase, index) => (
                    <div key={phase.name} className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <div className="flex justify-between items-center mb-2">
                        <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{index + 1}. {phase.name}</h4>
                        <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {phase.actionCount} {t('common.actions')} &bull; {phase.successfulCount} {t('actions.successful').toLowerCase()}
                        </span>
                      </div>
                      {phase.keyActions.length > 0 && (
                        <div className="mt-2">
                          <ul className={`text-sm space-y-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                            {phase.keyActions.map((ka, i) => (
                              <li
                                key={i}
                                className="cursor-pointer hover:text-blue-500 transition-colors"
                                onClick={() => ka.originalAction && setSelectedActionForModal(ka.originalAction)}
                              >
                                &bull; {typeof ka === 'string' ? ka : ka.title}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {debrief.attackPath.criticalPaths && debrief.attackPath.criticalPaths.length > 0 && (
                <div>
                  <h3 className={`font-semibold mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{t('debrief.criticalPaths')}</h3>
                  <div className="space-y-3">
                    {debrief.attackPath.criticalPaths.map((path, index) => (
                      <div
                        key={index}
                        className={`p-4 rounded-lg border ${
                          path.severity === 'critical'
                            ? (isDarkMode ? 'bg-red-900/20 border-red-500' : 'bg-red-50 border-red-200')
                            : (isDarkMode ? 'bg-orange-900/20 border-orange-500' : 'bg-orange-50 border-orange-200')
                        }`}
                      >
                        <h4 className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{path.name}</h4>
                        <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{path.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Detailed Actions */}
          {activeSection === 'actions' && debrief.detailedActions && (
            <div>
              <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faClipboardList} className="text-blue-800" />
                {t('debrief.detailedActions')}
              </h2>

              <div className="space-y-4">
                {debrief.detailedActions.map((action) => (
                  <div
                    key={action.id}
                    className={`border rounded-lg overflow-hidden ${
                      action.severity === 'critical' ? (isDarkMode ? 'border-red-500' : 'border-red-300') :
                      action.severity === 'high' ? (isDarkMode ? 'border-orange-500' : 'border-orange-300') :
                      (isDarkMode ? 'border-gray-700' : 'border-gray-200')
                    }`}
                  >
                    <div
                      className={`p-4 cursor-pointer ${isDarkMode ? 'bg-gray-700 hover:bg-gray-650' : 'bg-gray-50 hover:bg-gray-100'}`}
                      onClick={() => setSelectedActionForModal(action.originalAction || action)}
                    >
                      <div className="flex justify-between items-start">
                        <div className="flex-1">
                          <h3 className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{action.title}</h3>
                          <div className={`flex items-center space-x-4 mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            <span>{new Date(action.timestamp).toLocaleString()}</span>
                            <span>{action.operator}</span>
                            <span>{action.duration}</span>
                            {action.technique && (
                              <span className="text-purple-500">{action.technique}</span>
                            )}
                          </div>
                        </div>
                        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                          action.result === 'successful' ? (isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800') :
                          action.result === 'detected' ? (isDarkMode ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-100 text-yellow-800') :
                          action.result === 'blocked' ? (isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-800') :
                          (isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-800')
                        }`}>
                          {action.result}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Timeline - existing implementation */}
          {activeSection === 'timeline' && (
            <div>
              <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faClock} className="text-blue-800" />
                {t('debrief.sections.timeline')}
              </h2>
              <div className="space-y-3">
                {debrief.timeline.map((event, index) => (
                  <div
                    key={index}
                    className={`border-l-4 pl-4 py-3 cursor-pointer ${
                      event.critical ? `border-red-500 ${isDarkMode ? 'bg-red-900/20' : 'bg-red-50'}` :
                      event.result === 'successful' ? 'border-green-500' :
                      event.result === 'detected' ? 'border-yellow-500' :
                      (isDarkMode ? 'border-gray-600' : 'border-gray-300')
                    } ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
                    onClick={() => setSelectedActionForModal(event.originalAction || event)}
                  >
                    <div className="flex justify-between items-start">
                      <div>
                        <div className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{event.action}</div>
                        <div className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {new Date(event.timestamp).toLocaleString()} &bull;
                          {event.duration} &bull;
                          {event.operator}
                        </div>
                        <div className={`text-sm mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {event.technique}
                        </div>
                      </div>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        event.result === 'successful' ? (isDarkMode ? 'bg-green-900/40 text-green-300' : 'bg-green-100 text-green-800') :
                        event.result === 'detected' ? (isDarkMode ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-100 text-yellow-800') :
                        event.result === 'blocked' ? (isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-100 text-red-800') :
                        (isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-100 text-gray-800')
                      }`}>
                        {event.result}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Statistics - existing implementation */}
          {activeSection === 'statistics' && (
            <div>
              <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faChartLine} className="text-blue-800" />
                {t('debrief.engagementStatistics')}
              </h2>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{t('debrief.actionResults')}</h3>
                  <div className="space-y-2">
                    {Object.entries(debrief.statistics.results).map(([result, count]) => (
                      <div key={result} className={`flex justify-between items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                        <span className="capitalize">{result}:</span>
                        <span className="font-medium">{count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{t('debrief.performanceMetrics')}</h3>
                  <div className={`space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <div className="flex justify-between">
                      <span>{t('reports.successRate')}:</span>
                      <span className="font-medium">{Math.round(debrief.statistics.successRate * 100)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('debrief.detectionRate')}:</span>
                      <span className="font-medium">{Math.round(debrief.statistics.detectionRate * 100)}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span>{t('reports.targetsCompromised')}:</span>
                      <span className="font-medium">{debrief.statistics.targetsCompromised}/{debrief.statistics.totalTargets}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{t('debrief.mostUsedTechniques')}</h3>
                  <div className={`space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {debrief.statistics.mostUsedTechniques.map((item, index) => (
                      <div key={index} className="flex justify-between">
                        <span>{item.technique}</span>
                        <span className="font-medium">{item.count}</span>
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{t('debrief.operatorPerformance')}</h3>
                  <div className={`space-y-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    {Object.entries(debrief.statistics.actionsByOperator).map(([operator, stats]) => (
                      <div key={operator} className="flex justify-between">
                        <span>{operator}:</span>
                        <span className="font-medium">{stats.successful}/{stats.total}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* SIEM Import Accountability Stats */}
              {debrief.siemImportStats && debrief.siemImportStats.totalImported > 0 && (
                <div className={`mt-8 pt-6 border-t ${isDarkMode ? 'border-gray-700' : 'border-gray-200'}`}>
                  <h3 className={`text-lg font-semibold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>
                    {t('debrief.siemImportAccountability') || 'SIEM Import Accountability'}
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('siemReview.totalImported') || 'Total Imported'}</p>
                      <p className={`text-2xl font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{debrief.siemImportStats.totalImported}</p>
                    </div>
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <p className="text-sm text-teal-500">{t('siemReview.targetMatched') || 'Target Matched'}</p>
                      <p className="text-2xl font-bold text-teal-600">{debrief.siemImportStats.targetMatched}</p>
                    </div>
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <p className="text-sm text-green-500">{t('siemReview.confirmed') || 'Confirmed'}</p>
                      <p className="text-2xl font-bold text-green-600">{debrief.siemImportStats.confirmed}</p>
                    </div>
                    <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                      <p className="text-sm text-purple-500">{t('debrief.correlationMatched') || 'Correlated with Red Team'}</p>
                      <p className="text-2xl font-bold text-purple-600">{debrief.siemImportStats.correlationMatched}</p>
                    </div>
                  </div>
                  <div className={`mt-4 p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                      <div>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('debrief.reviewRate') || 'Review Rate'}</p>
                        <p className={`text-lg font-bold ${debrief.siemImportStats.reviewRate >= 80 ? 'text-green-600' : debrief.siemImportStats.reviewRate >= 50 ? 'text-yellow-600' : 'text-red-600'}`}>
                          {debrief.siemImportStats.reviewRate}%
                        </p>
                      </div>
                      <div>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('debrief.confirmRate') || 'Confirm Rate'}</p>
                        <p className={`text-lg font-bold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{debrief.siemImportStats.confirmRate}%</p>
                      </div>
                      <div>
                        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('siemReview.pendingReview') || 'Still Pending'}</p>
                        <p className={`text-lg font-bold ${debrief.siemImportStats.pendingReview > 0 ? 'text-yellow-600' : 'text-green-600'}`}>
                          {debrief.siemImportStats.pendingReview}
                        </p>
                      </div>
                    </div>
                    {debrief.siemImportStats.pendingReview > 0 && (
                      <p className={`mt-2 text-sm ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'}`}>
                        {t('debrief.pendingWarning') || `${debrief.siemImportStats.pendingReview} imported alerts have not yet been reviewed by the blue team.`}
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Other sections remain the same... */}
          {activeSection === 'findings' && (
            <div>
              <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faSearch} className="text-blue-800" />
                {t('debrief.sections.findings')}
              </h2>

              {debrief.findings?.critical?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-red-500">{t('debrief.criticalFindings')}</h3>
                  <div className="space-y-3">
                    {debrief.findings.critical.map((finding, index) => (
                      <div key={index} className={`border rounded-lg p-4 ${isDarkMode ? 'bg-red-900/20 border-red-500' : 'bg-red-50 border-red-200'}`}>
                        <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{finding.title}</h4>
                        <p className={`mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{finding.description}</p>
                        <div className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          <span className="font-medium">{t('debrief.impact')}:</span> {finding.impact}
                        </div>
                        {finding.affectedAssets?.length > 0 && (
                          <div className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                            <span className="font-medium">{t('debrief.affectedAssets')}:</span> {finding.affectedAssets.join(', ')}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {debrief.findings?.high?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-orange-500">{t('debrief.highFindings')}</h3>
                  <div className="space-y-3">
                    {debrief.findings.high.map((finding, index) => (
                      <div key={index} className={`border rounded-lg p-4 ${isDarkMode ? 'bg-orange-900/20 border-orange-500' : 'bg-orange-50 border-orange-200'}`}>
                        <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{finding.title}</h4>
                        <p className={`mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{finding.description}</p>
                        <div className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          <span className="font-medium">{t('debrief.impact')}:</span> {finding.impact}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {debrief.findings?.medium?.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-yellow-500">{t('debrief.mediumFindings')}</h3>
                  <div className="space-y-3">
                    {debrief.findings.medium.map((finding, index) => (
                      <div key={index} className={`border rounded-lg p-4 ${isDarkMode ? 'bg-yellow-900/20 border-yellow-500' : 'bg-yellow-50 border-yellow-200'}`}>
                        <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{finding.title}</h4>
                        <p className={`mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{finding.description}</p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeSection === 'technical' && (
            <div>
              <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faWrench} className="text-blue-800" />
                {t('debrief.technicalDetails')}
              </h2>

              <div className="mb-6">
                <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{t('debrief.techniquesAnalysis')}</h3>
                <div className="overflow-x-auto">
                  <table className={`min-w-full divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                    <thead className={isDarkMode ? 'bg-gray-900' : 'bg-gray-50'}>
                      <tr>
                        <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('attackChain.technique')}</th>
                        <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('actions.tactic')}</th>
                        <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('debrief.attempts')}</th>
                        <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('reports.successRate')}</th>
                        <th className={`px-4 py-2 text-left text-xs font-medium uppercase tracking-wider ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{t('debrief.detectionRate')}</th>
                      </tr>
                    </thead>
                    <tbody className={`divide-y ${isDarkMode ? 'divide-gray-700' : 'divide-gray-200'}`}>
                      {Object.entries(debrief.technicalDetails.techniques).map(([id, tech]) => (
                        <tr
                          key={id}
                          className={`cursor-pointer ${isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'}`}
                          onClick={() => {
                            const actionWithOrig = tech.actions.find(a => a.originalAction);
                            if (actionWithOrig) setSelectedActionForModal(actionWithOrig.originalAction);
                          }}
                        >
                          <td className="px-4 py-2 whitespace-nowrap text-sm">
                            <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{id}</div>
                            <div className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>{tech.name}</div>
                          </td>
                          <td className={`px-4 py-2 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{tech.tactic}</td>
                          <td className={`px-4 py-2 whitespace-nowrap text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{tech.actions.length}</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-green-500">{Math.round(tech.successRate * 100)}%</td>
                          <td className="px-4 py-2 whitespace-nowrap text-sm text-yellow-500">{Math.round(tech.detectionRate * 100)}%</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>

              {debrief.technicalDetails.tools.length > 0 && (
                <div className="mb-6">
                  <h3 className={`text-lg font-semibold mb-3 ${isDarkMode ? 'text-gray-200' : 'text-gray-900'}`}>{t('debrief.toolsMentioned')}</h3>
                  <div className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                    <ul className={`list-disc list-inside space-y-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      {debrief.technicalDetails.tools.map((tool, index) => (
                        <li key={index} className="text-sm">{tool}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              )}
            </div>
          )}

          {activeSection === 'recommendations' && (
            <div>
              <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faLightbulb} className="text-blue-800" />
                {t('debrief.sections.recommendations')}
              </h2>

              <div className="space-y-4">
                {debrief.recommendations.map((rec, index) => (
                  <div
                    key={index}
                    className={`border rounded-lg p-4 ${
                      rec.priority === 'immediate' ? (isDarkMode ? 'border-red-500 bg-red-900/20' : 'border-red-300 bg-red-50') :
                      rec.priority === 'high' ? (isDarkMode ? 'border-orange-500 bg-orange-900/20' : 'border-orange-300 bg-orange-50') :
                      rec.priority === 'medium' ? (isDarkMode ? 'border-yellow-500 bg-yellow-900/20' : 'border-yellow-300 bg-yellow-50') :
                      (isDarkMode ? 'border-gray-600 bg-gray-700' : 'border-gray-300')
                    }`}
                  >
                    <div className="flex justify-between items-start mb-2">
                      <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{rec.title}</h4>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        rec.priority === 'immediate' ? (isDarkMode ? 'bg-red-900/40 text-red-300' : 'bg-red-200 text-red-800') :
                        rec.priority === 'high' ? (isDarkMode ? 'bg-orange-900/40 text-orange-300' : 'bg-orange-200 text-orange-800') :
                        rec.priority === 'medium' ? (isDarkMode ? 'bg-yellow-900/40 text-yellow-300' : 'bg-yellow-200 text-yellow-800') :
                        (isDarkMode ? 'bg-gray-600 text-gray-300' : 'bg-gray-200 text-gray-800')
                      }`}>
                        {({'immediate': t('debrief.priorityImmediate') || 'Immediate', 'high': t('findings.high') || 'High', 'medium': t('findings.medium') || 'Medium', 'low': t('findings.low') || 'Low'})[rec.priority] || rec.priority}
                      </span>
                    </div>
                    <p className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{rec.description}</p>
                    <div className={`mt-2 flex gap-4 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      <span>{t('debrief.effort')}: {rec.effort}</span>
                      <span>{t('debrief.impact')}: {rec.impact}</span>
                      <span>{rec.category}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {activeSection === 'lessons' && (
            <div>
              <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faBook} className="text-blue-800" />
                {t('debrief.sections.lessons')}
              </h2>

              <div className="mb-6">
                <h3 className="text-lg font-semibold mb-3 text-green-500">{t('debrief.successes')}</h3>
                <div className="space-y-3">
                  {debrief.lessonsLearned.successes.map((lesson, index) => (
                    <div key={index} className={`rounded-lg p-4 ${isDarkMode ? 'bg-green-900/20' : 'bg-green-50'}`}>
                      <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{lesson.description}</div>
                      <div className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                        {t('debrief.recommendation')}: {lesson.recommendation}
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {debrief.lessonsLearned.challenges.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-orange-500">{t('debrief.challenges')}</h3>
                  <div className="space-y-3">
                    {debrief.lessonsLearned.challenges.map((lesson, index) => (
                      <div key={index} className={`rounded-lg p-4 ${isDarkMode ? 'bg-orange-900/20' : 'bg-orange-50'}`}>
                        <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{lesson.description}</div>
                        <div className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {t('debrief.recommendation')}: {lesson.recommendation}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {debrief.lessonsLearned.improvements.length > 0 && (
                <div className="mb-6">
                  <h3 className="text-lg font-semibold mb-3 text-blue-500">{t('debrief.improvements')}</h3>
                  <div className="space-y-3">
                    {debrief.lessonsLearned.improvements.map((lesson, index) => (
                      <div key={index} className={`rounded-lg p-4 ${isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50'}`}>
                        <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{lesson.description}</div>
                        <div className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                          {t('debrief.recommendation')}: {lesson.recommendation}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {activeSection === 'future' && (
            <div>
              <h2 className={`text-2xl font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                <FontAwesomeIcon icon={faGlobe} className="text-blue-800" />
                {t('debrief.futureConsiderations')}
              </h2>

              <div className="space-y-4">
                {debrief.futureConsiderations.map((consideration, index) => (
                  <div key={index} className={`border rounded-lg p-4 ${isDarkMode ? 'bg-purple-900/20 border-purple-500' : 'bg-purple-50 border-purple-200'}`}>
                    <h4 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{consideration.title}</h4>
                    <p className={`mt-1 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{consideration.description}</p>
                    <div className={`mt-2 text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                      <span className="font-medium">{t('debrief.recommendation')}:</span> {consideration.recommendation}
                    </div>
                    <div className={`mt-1 text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-600'}`}>
                      {consideration.category}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Action Detail Modal */}
      <ActionDetailModal
        action={selectedActionForModal}
        isOpen={!!selectedActionForModal}
        onClose={() => setSelectedActionForModal(null)}
        projectId={projectId}
        onActionUpdated={(updated) => setSelectedActionForModal(updated)}
      />
    </div>
  );
};

export default DebriefViewer;